import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Award, Loader2, GraduationCap, Pencil } from 'lucide-react';
import { LigneDepliable } from './ApercuLignes';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLE = {
  certifie: 'bg-emerald-100 text-emerald-800',
  passe_af: 'bg-indigo-100 text-indigo-800',
  passe_aft: 'bg-cyan-100 text-cyan-800',
  aft_a_venir: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
  aft_non_reparti: 'bg-amber-50 text-amber-800 border border-amber-300',
  absent_aft: 'bg-red-100 text-red-700',
  absent: 'bg-red-100 text-red-700',
  reporte: 'bg-amber-100 text-amber-800',
  dispense: 'bg-slate-200 text-slate-600',
  a_venir: 'bg-slate-100 text-slate-500',
  non_renseigne: 'bg-orange-100 text-orange-700',
};
const fmt = (d) => (d ? d.split('-').reverse().join('/') : '—');

const Compteur = ({ label, value, cls, testId }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`} data-testid={testId}>{value} <span className="font-normal">{label}</span></span>
);

const ModuleBloc = ({ m, dossierId, onOpenCandidat }) => {
  const navigate = useNavigate();
  const c = m.compteurs;
  return (
    <div className="rounded-lg border border-slate-200" data-testid={`suivi-cert-module-${m.module.id}`}>
      <div className="px-4 py-2.5 bg-slate-50 rounded-t-lg flex flex-wrap items-center gap-2">
        <span className="text-[9px] font-bold text-rose-700 bg-rose-100 px-1 py-0.5 rounded">CERT</span>
        <span className="text-sm font-semibold text-slate-800"><span className="font-mono text-xs text-slate-500 mr-1">{m.module.code}</span>{m.module.titre}</span>
        <span className="text-xs text-slate-500">· sur l'AF : {m.dates.length ? m.dates.map(fmt).join(', ') : 'non planifié'}</span>
        {m.afts.map(a => (
          <button key={a.id} type="button" onClick={() => navigate(`/dossiers/${a.id}`)} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-cyan-600 text-white hover:bg-cyan-700" data-testid={`suivi-cert-aft-${a.id}`}>
            <GraduationCap className="w-3 h-3" /> {a.code} · {a.dates?.length > 1 ? `${a.dates.length} jours` : fmt(a.date)} · {a.nb_candidats}{a.nb_non_repartis > 0 ? ` · ${a.nb_non_repartis} à répartir` : ''}
          </button>
        ))}
      </div>
      <div className="px-4 py-2 flex flex-wrap gap-1.5 border-b" data-testid={`suivi-cert-compteurs-${m.module.id}`}>
        <Compteur label="candidat(s)" value={c.total} cls="bg-slate-100 text-slate-700" testId={`cpt-total-${m.module.id}`} />
        <Compteur label="passé(s) sur l'AF" value={c.passe_af} cls={STYLE.passe_af} testId={`cpt-passe-af-${m.module.id}`} />
        <Compteur label="passé(s) via AFT" value={c.passe_aft} cls={STYLE.passe_aft} testId={`cpt-passe-aft-${m.module.id}`} />
        <Compteur label="certifié(s)" value={c.certifies} cls={STYLE.certifie} testId={`cpt-certifies-${m.module.id}`} />
        <Compteur label="reste(nt) à passer" value={c.reste_a_passer} cls="bg-amber-100 text-amber-800" testId={`cpt-reste-${m.module.id}`} />
        <Compteur label="absent(s)" value={c.absents} cls={STYLE.absent} testId={`cpt-absents-${m.module.id}`} />
        <Compteur label="dispensé(s)" value={c.dispenses} cls={STYLE.dispense} testId={`cpt-dispenses-${m.module.id}`} />
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[640px] text-xs">
          <div className="grid grid-cols-[1.6fr_1.2fr_0.8fr_1fr_1.4fr] gap-2 px-4 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 border-b">
            <span>Candidat</span><span>Statut</span><span>Date</span><span>AFT</span><span>Résultats</span>
          </div>
          {m.candidats.map(r => (
            <div key={r.candidat_id} className={`grid grid-cols-[1.6fr_1.2fr_0.8fr_1fr_1.4fr] gap-2 px-4 py-1.5 items-center border-b last:border-b-0 ${r.reste_a_passer ? 'bg-amber-50/40' : ''}`} data-testid={`suivi-cert-row-${m.module.id}-${r.candidat_id}`}>
              <span className="flex items-center gap-1 min-w-0">
                <button type="button" onClick={() => onOpenCandidat?.(r.candidat_id)} className="font-medium text-slate-800 hover:text-indigo-700 truncate text-left">{r.nom} {r.prenom}</button>
                <button type="button" onClick={() => navigate(`/dossiers/${dossierId}/candidat/${r.candidat_id}?from=apercu`)} className="p-0.5 text-slate-300 hover:text-indigo-600 shrink-0" title="Fiche candidat"><Pencil className="w-3 h-3" /></button>
              </span>
              <span><span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${STYLE[r.statut] || ''}`} data-testid={`suivi-cert-statut-${m.module.id}-${r.candidat_id}`}>{r.label}{r.statut === 'certifie' && r.via === 'aft' ? ' (AFT)' : ''}</span></span>
              <span className="text-slate-600">{fmt(r.statut === 'reporte' ? r.report_date : r.date)}</span>
              <span>{r.aft_code ? <button type="button" onClick={() => navigate(`/dossiers/${r.aft_id}`)} className="font-mono text-[10px] text-cyan-700 hover:underline">{r.aft_code}</button> : <span className="text-slate-300">—</span>}</span>
              <span className="flex flex-wrap gap-1">
                {r.resultats.length === 0 && <span className="text-slate-300">—</span>}
                {r.resultats.map((x, i) => (
                  <span key={i} className={`px-1.5 py-0.5 rounded text-[10px] ${x.pourcentage == null ? 'bg-slate-100 text-slate-500' : x.pourcentage >= 50 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`} title={`${x.type} · ${x.titre} · ${fmt(x.date)}`}>
                    {x.type === 'QCM' ? 'QCM' : 'Éval'} {x.pourcentage == null ? '—' : `${Math.round(x.pourcentage)} %`}
                  </span>
                ))}
              </span>
            </div>
          ))}
          {m.candidats.length === 0 && <p className="px-4 py-3 text-slate-400 italic">Aucun candidat inscrit.</p>}
        </div>
      </div>
    </div>
  );
};

// Section « Suivi de certification » de l'aperçu d'une AF mère : qui a passé / reste à passer chaque module CERT
export const SuiviCertification = ({ dossierId, onOpenCandidat, refreshKey }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axios.get(`${API_URL}/dossiers/${dossierId}/suivi-certification`).then(r => setData(r.data)).catch(() => setData({ modules: [] }));
  }, [dossierId, refreshKey]);
  if (data && data.modules.length === 0) return null;
  const reste = data ? data.modules.reduce((s, m) => s + m.compteurs.reste_a_passer, 0) : 0;
  const cert = data ? data.modules.reduce((s, m) => s + m.compteurs.certifies, 0) : 0;
  return (
    <LigneDepliable icon={Award} testId="apercu-suivi-certification" titre="Suivi de certification"
      resume={data && <span className="flex items-center gap-2 text-xs font-normal"><span className="text-slate-500">{data.modules.length} module(s) certifiant(s)</span>{reste > 0 && <span className="text-amber-700 font-semibold" data-testid="suivi-cert-reste-total">{reste} reste(nt) à passer</span>}{cert > 0 && <span className="text-emerald-700">{cert} certifié(s)</span>}</span>}
      droite={<span>Qui a passé / reste à passer</span>}>
      {!data ? <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div> : (
        <div className="space-y-3">{data.modules.map(m => <ModuleBloc key={m.module.id} m={m} dossierId={dossierId} onOpenCandidat={onOpenCandidat} />)}</div>
      )}
    </LigneDepliable>
  );
};
